import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "flex flex-col items-center gap-2" };
const _hoisted_2 = { class: "flex flex-col items-center gap-0.5 text-center" };
const _hoisted_3 = ["id"];
const _hoisted_4 = ["id"];
const _hoisted_5 = { class: "flex flex-col gap-5" };
const _hoisted_6 = {
    key: 0,
    class: "flex justify-center py-6",
    role: "status"
};
const _hoisted_7 = { class: "sr-only" };
const _hoisted_8 = {
    key: 0,
    class: "border border-(--border-border-base-soft) rounded-(--border-border-radius-rounded-l) shadow-s bg-white flex flex-col py-4 gap-4"
};
const _hoisted_9 = { class: "text-[length:var(--size-m)] font-[var(--weight-semi-bold)] text-(--text-text-body) text-center px-4" };
const _hoisted_10 = { class: "flex flex-col gap-4" };
const _hoisted_11 = { class: "flex flex-col gap-px sm:flex-1" };
const _hoisted_12 = { class: "text-[length:var(--size-m)] font-[var(--weight-medium)] text-(--text-text-body)" };
const _hoisted_13 = { class: "flex items-center gap-2 px-3 py-2 bg-(--background-background-white-secondary-soft) border border-(--border-border-base-soft) rounded-(--border-border-radius-rounded-l)" };
const _hoisted_14 = { class: "text-[length:var(--size-m)] text-(--text-text-body) truncate" };
const _hoisted_15 = { class: "flex items-center gap-2 shrink-0" };
const _hoisted_16 = { class: "min-w-[6.5rem]" };
const _hoisted_17 = { class: "min-w-[5.5rem]" };
import { computed, ref, watch, useId } from "vue";
import { Alert, Avatar, Badge, Button, Modal } from "@eudonet/opal";
import { useAppToast } from "@/shared/composables/useAppToast";
import { usePermissions } from "@/shared/composables/usePermissions";
import { getRelationDeletePreview, deleteContactRelation } from "../../infrastructure/api/contactRelationsApi";
import { logger } from "@/infrastructure/services/LoggerService";
import ModuleState from "@/shared/components/ModuleState.vue";
// ─── Props / emits ─────────────────────────────────────────────────────────────
export default /*@__PURE__*/ _defineComponent({
    __name: 'DeleteRelationConfirmationModal',
    props: /*@__PURE__*/ _mergeModels({
        contactId: {},
        organisation: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["deleted"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const open = _useModel(__props, "modelValue");
        const props = __props;
        const emit = __emit;
        // ─── Dependencies ──────────────────────────────────────────────────────────────
        const { canEditOphub: canEdit } = usePermissions();
        const { toastSuccess, toastError } = useAppToast();
        const titleId = useId();
        const descriptionId = useId();
        // ─── Preview ───────────────────────────────────────────────────────────────────
        const preview = ref(null);
        const previewLoading = ref(false);
        const previewError = ref(false);
        async function loadPreview() {
            previewLoading.value = true;
            previewError.value = false;
            try {
                preview.value = await getRelationDeletePreview(props.contactId, props.organisation.id);
            }
            catch (err) {
                logger.error("DeleteRelationConfirmationModal: preview load failed", err);
                previewError.value = true;
            }
            finally {
                previewLoading.value = false;
            }
        }
        // immediate: true because the modal is mounted via v-if with open already true —
        // a non-immediate watch would miss the first open and never call loadPreview
        watch(open, (v) => {
            if (v) {
                preview.value = null;
                loadPreview();
            }
        }, { immediate: true });
        const COORD_ICONS = {
            email: "ri-mail-send-line",
            phone: "ri-phone-fill",
            address: "ri-map-pin-2-line",
        };
        const COORD_TYPE_LABEL_KEYS = {
            email: "contact.detail.relations.delete.coordinates.type.email",
            phone: "contact.detail.relations.delete.coordinates.type.phone",
            home: "contact.detail.addresses.types.Home",
            work: "contact.detail.addresses.types.Work",
            billing: "contact.detail.addresses.types.Billing",
            shipping: "contact.detail.addresses.types.Shipping",
            headquarters: "contact.detail.addresses.types.Headquarters",
            branch: "contact.detail.addresses.types.Branch",
        };
        const COORD_CONTEXT_LABEL_KEYS = {
            mobile: "contact.detail.addPhone.subType.mobile",
            fixe: "contact.detail.addPhone.subType.fixe",
            personal: "contact.detail.addEmail.context.personal",
            professional: "contact.detail.addEmail.context.professional",
        };
        function coordIcon(kind) {
            return COORD_ICONS[kind] ?? "ri-link";
        }
        const hasPrimaryCoordinate = computed(() => preview.value?.coordinates.some((c) => c.isPrimary) ?? false);
        // ─── Delete ────────────────────────────────────────────────────────────────────
        const deleting = ref(false);
        async function handleDelete() {
            if (!canEdit.value)
                return;
            deleting.value = true;
            try {
                await deleteContactRelation(props.contactId, props.organisation.id);
                toastSuccess("contact.detail.relations.delete.success");
                emit("deleted");
                open.value = false;
            }
            catch (err) {
                logger.error("DeleteRelationConfirmationModal: delete failed", err);
                toastError("contact.detail.relations.delete.error");
            }
            finally {
                deleting.value = false;
            }
        }
        return (_ctx, _cache) => {
            const _component_i18n_t = _resolveComponent("i18n-t");
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((open).value = $event)),
                size: "md",
                "header-position": "center",
                "footer-position": "split",
                "show-close-button": true,
                "show-header-border": false,
                "show-footer-border": false,
                "aria-labelledby": _unref(titleId),
                "aria-describedby": _unref(descriptionId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createVNode(_unref(Avatar), {
                            img: __props.organisation.logoUrl ?? undefined,
                            name: __props.organisation.name,
                            size: "md",
                            entity: "organisation",
                            "avatar-style": "square",
                            logo: "",
                            presentational: ""
                        }, null, 8 /* PROPS */, ["img", "name"]),
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("h2", {
                                id: _unref(titleId),
                                class: "text-[length:var(--size-xxl)] font-[var(--weight-bold)]"
                            }, _toDisplayString(__props.organisation.name), 9 /* TEXT, PROPS */, _hoisted_3),
                            _createElementVNode("p", {
                                id: _unref(descriptionId),
                                class: "text-[length:var(--size-l)] text-(--text-text-body-subtle)"
                            }, _toDisplayString(_ctx.$t("contact.detail.relations.delete.subtitle")), 9 /* TEXT, PROPS */, _hoisted_4)
                        ])
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _ctx.$t('common.cancel'),
                        onClick: _cache[0] || (_cache[0] = ($event) => (open.value = false))
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "error",
                        "icon-left": "ri-delete-bin-line",
                        label: _ctx.$t('contact.detail.relations.delete.confirm'),
                        loading: deleting.value,
                        disabled: !_unref(canEdit) || previewLoading.value || !!previewError.value || !preview.value,
                        onClick: handleDelete
                    }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                        _createVNode(_component_i18n_t, {
                            keypath: "contact.detail.relations.delete.body",
                            tag: "p",
                            class: "text-[length:var(--size-l)] text-(--text-text-body) text-center leading-[var(--line-height-xxl)]"
                        }, {
                            bold1: _withCtx(() => [
                                _createElementVNode("strong", null, _toDisplayString(_ctx.$t("contact.detail.relations.delete.bodyBold1")), 1 /* TEXT */)
                            ]),
                            bold2: _withCtx(() => [
                                _createElementVNode("strong", null, _toDisplayString(_ctx.$t("contact.detail.relations.delete.bodyBold2")), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                        }),
                        (previewLoading.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                _cache[2] || (_cache[2] = _createElementVNode("i", {
                                    class: "ri-loader-4-line animate-spin text-3xl text-(--text-text-body-subtle)",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t("common.loading")), 1 /* TEXT */)
                            ]))
                            : (previewError.value)
                                ? (_openBlock(), _createBlock(ModuleState, {
                                    key: 1,
                                    type: "error",
                                    title: _ctx.$t('contact.detail.relations.delete.loadError.title'),
                                    "cta-icon": "ri-refresh-line",
                                    "cta-label": _ctx.$t('common.retry'),
                                    onCta: loadPreview
                                }, null, 8 /* PROPS */, ["title", "cta-label"]))
                                : (preview.value)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                        (preview.value.coordinates.length > 0)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                                _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.$t("contact.detail.relations.delete.coordinatesTitle")), 1 /* TEXT */),
                                                _cache[3] || (_cache[3] = _createElementVNode("hr", { class: "border-t border-(--border-border-base-soft)" }, null, -1 /* CACHED */)),
                                                _createElementVNode("div", _hoisted_10, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(preview.value.coordinates, (coord) => {
                                                        return (_openBlock(), _createElementBlock("div", {
                                                            key: coord.id,
                                                            class: "flex flex-col gap-y-2 sm:flex-row sm:items-end sm:gap-x-5 sm:gap-y-0 px-4"
                                                        }, [
                                                            _createElementVNode("div", _hoisted_11, [
                                                                _createElementVNode("span", _hoisted_12, _toDisplayString(coord.type ? _ctx.$t(COORD_TYPE_LABEL_KEYS[coord.type] ?? coord.type) : ''), 1 /* TEXT */),
                                                                _createElementVNode("div", _hoisted_13, [
                                                                    _createElementVNode("i", {
                                                                        class: _normalizeClass([coordIcon(coord.kind), "text-[length:var(--size-m)] text-(--text-text-body-subtle) shrink-0"]),
                                                                        "aria-hidden": "true"
                                                                    }, null, 2 /* CLASS */),
                                                                    _createElementVNode("span", _hoisted_14, _toDisplayString(coord.value), 1 /* TEXT */)
                                                                ])
                                                            ]),
                                                            _createElementVNode("div", _hoisted_15, [
                                                                _createElementVNode("div", _hoisted_16, [
                                                                    _createVNode(_unref(Badge), {
                                                                        theme: "neutral",
                                                                        label: coord.context ? _ctx.$t(COORD_CONTEXT_LABEL_KEYS[coord.context] ?? coord.context) : '',
                                                                        "show-close-icon": false,
                                                                        "show-left-icon": false,
                                                                        size: "sm"
                                                                    }, null, 8 /* PROPS */, ["label"])
                                                                ]),
                                                                _createElementVNode("div", _hoisted_17, [
                                                                    (coord.isPrimary)
                                                                        ? (_openBlock(), _createBlock(_unref(Badge), {
                                                                            key: 0,
                                                                            theme: "yellow",
                                                                            label: _ctx.$t('contact.detail.relations.delete.primaryBadge'),
                                                                            "show-close-icon": false,
                                                                            size: "sm",
                                                                            "left-icon": "ri-star-line"
                                                                        }, null, 8 /* PROPS */, ["label"]))
                                                                        : _createCommentVNode("v-if", true)
                                                                ])
                                                            ])
                                                        ]));
                                                    }), 128 /* KEYED_FRAGMENT */))
                                                ])
                                            ]))
                                            : _createCommentVNode("v-if", true),
                                        (hasPrimaryCoordinate.value)
                                            ? (_openBlock(), _createBlock(_unref(Alert), {
                                                key: 1,
                                                color: "warning",
                                                heading: _ctx.$t('contact.detail.relations.delete.primaryAlert')
                                            }, null, 8 /* PROPS */, ["heading"]))
                                            : _createCommentVNode("v-if", true)
                                    ], 64 /* STABLE_FRAGMENT */))
                                    : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby", "aria-describedby"]));
        };
    }
});
